import {
  DefaultError,
  keepPreviousData,
  QueryClient,
  useQuery,
  UseQueryOptions,
  UseQueryResult,
} from "@tanstack/react-query";
import { APIError, APIPromise, APIResponse } from "services/HttpService";
import { easasAlert } from "../../components/fragments/easas-global-fragment/EasasGlobalAlerts";

export interface IQueryOptions<T = any> {
  enabled?: boolean;
  noCache?: boolean;
  gcTime?: number;
  staleTime?: number;
  refetchInterval?: number | boolean;
  onSuccess?: (data?: T) => void;
  onError?: (data?: T) => void;
  retry?: boolean | number;
  refetchOnWindowFocus?: boolean;
  keepPreviousData?: boolean;
}

export interface IMutationOptions {
  onSuccess?: (data?: any) => void;
  onFailure?: (data?: any) => void;
  onMutate?: (data?: any) => void;
}

export enum QueryKey {
  ALERT_FAN = "alert-fan",
  ALERT_FAN_SILENCE = "alert-fan-silence",
  ALERTS_AGGREGATORS = "alerts-aggregators",
  ALERTS_SENDERS = "alerts-senders",
  AIRFLOW_DAG_TASK_INFOS = "airflow-dag-task-infos",
  BASE_DATASETS = "base-datasets",
  BUBBLE_CHART_DATA = "bubble-chart-data",
  CATEGORIZED_DATASET = "categorized-dataset",
  CLEAR_DATASET = "clear-dataset",
  COMPANIES = "companies",
  DASHBOARD = "dashboard",
  DASHBOARDS_MANAGEMENT = "dashboards-management",
  DASHBOARD_HISTORY = "dashboard-history",
  DASHBOARD_STATUS = "dashboard-status",
  DATASET_CATEGORIES = "dataset-categories",
  DATASET_DATA_INFO = "dataset-data-info",
  DATASET_DATA_RECENT = "dataset-data-recent",
  DATASET_GROUPS = "dataset-groups",
  DATASET_INFOS = "dataset-infos",
  DATASET_MAPPERS = "dataset-mappers",
  DATASET_USAGE_SEARCH = "dataset-usage-search",
  DATASETS_COUNT = "count",
  DEFAULT_CATEGORY = "default-category",
  EASASJS_BUILDS = "easasjs-builds",
  GENERATED_REPORT_DATA = "generated-report-data",
  GEO_MEASUREMENT_DEVICES = "geo-measurement-devices",
  INSTANT_JOBS = "instant-jobs",
  MEASUREMENT_DEVICES_BY_TAGS = "measurement-devices-by-tags",
  MEASUREMENT_DEVICES_READINGS = "measurement-devices-readings",
  OBJECT_DATASET = "object-dataset",
  OBJECT_DATASET_DATA = "object-dataset-data",
  PAGINATED_DATASETS = "paginated-datasets-list",
  PRODUCT_TEMPLATE_CONFIGS = "product-template-configs",
  PRODUCT_TEMPLATE_DATA = "product-template-data",
  PRODUCT_TEMPLATE_FOREIGN_KEYS = "product-template-foreign-keys",
  PRODUCT_TEMPLATE_INFOS = "product-template-infos",
  PRODUCT_TEMPLATE_PREVIEW_FILTER_DATA = "product-template-preview-filter-data",
  SCHEDULED_PYTHON_TASK_TEMPLATES = "scheduled-python-task-templates",
  SCHEDULED_TASK_DAG = "scheduled-task-dag",
  SCHEDULED_TASK_DAGS = "scheduled-task-dags",
  SCHEDULED_TASK_HISTORIES = "scheduled-task-histories",
  SCHEDULED_TASK_TEMPLATES = "scheduled-task-templates",
  SSO_TOKEN = "sso-token",
  COMPANY_USERS = "company-users",
  USER_INFO = "user-info",
  USER_SESSIONS = "user-sessions",
  USER_SETTINGS = "user-settings",
  GLOBALS_SUPPORTED_TIMEZONES = "globals-supported-timezones",
  MIGRATIONS = "migrations",
}

export function defaultResponseHandler<T>(
  res: APIResponse<T>,
  onSuccess?: (data?: T) => void,
  onError?: (error?: APIError) => void
): T | undefined {
  if (res.error) {
    console.error(res);
    onError && onError(res.error);
    throw res.error as Error;
  } else {
    onSuccess && onSuccess(res.data);
    return res.data;
  }
}

const defaultQueryOptions: IQueryOptions = {
  enabled: true,
  noCache: false,
  refetchInterval: false,
};

export const defaultMutationOptions: IMutationOptions = Object.freeze({
  onSuccess: () => {},
  onFailure: () => {},
  onMutate: () => {},
});

export const defaultDeleteMutationOptions: IMutationOptions = Object.freeze({
  onSuccess: () => {
    easasAlert({ summary: "Deleted", severity: "success" });
  },
  onFailure: () => {
    easasAlert({ summary: "Error deleting", severity: "error" });
  },
  onMutate: () => {},
});

export function useQueryWrapper<T>(
  queryKey: Array<QueryKey | string | null | undefined | number>,
  apiPromiseFn: () => APIPromise<T>,
  promiseHandler?: ((res: APIResponse<T>) => T) | null,
  options?: IQueryOptions,
  initialData?: T | (() => T) | undefined
): UseQueryResult<T, Error> {
  const usedOptions = { ...defaultQueryOptions, ...options };
  const usedPromiseHandler = promiseHandler ?? defaultResponseHandler;

  const queryOptions = {
    enabled: usedOptions.enabled,
    gcTime: usedOptions.gcTime ?? 1000 * 60 * 5,
    staleTime: usedOptions.staleTime ?? 1000 * 60,
    refetchInterval: usedOptions.refetchInterval,
    retry: usedOptions.retry,
    refetchOnWindowFocus: usedOptions.refetchOnWindowFocus,
    queryKey,
    placeholderData: usedOptions.keepPreviousData && keepPreviousData,
    initialData,
  } as UseQueryOptions<unknown, DefaultError, T>;

  if (usedOptions.noCache) {
    queryOptions.gcTime = 0;
    queryOptions.staleTime = 0;
  }

  return useQuery({
    queryFn: () =>
      apiPromiseFn().then(res => usedPromiseHandler(res, usedOptions.onSuccess, usedOptions.onError)),
    ...queryOptions,
  });
}

export const getRelatedCacheKeys = (queryClient: QueryClient, targetKey: string) => {
  return queryClient
    .getQueryCache()
    .getAll()
    .map(query => query.queryKey)
    .filter(key => key.includes(targetKey));
};
